import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

/**
 * Custom lock matching Supabase's expected signature:
 *   (name, acquireTimeout, fn) => Promise<R>
 * It MUST run `fn()` and return its result. We use the Navigator
 * LockManager with a timeout cap so cross-tab lock contention can
 * never freeze session initialization. If the lock can't be acquired
 * within the cap (or LockManager is unavailable), we run `fn` anyway.
 */
async function acquireLock<R>(
  name: string,
  _acquireTimeout: number,
  fn: () => Promise<R>,
): Promise<R> {
  if (typeof navigator === 'undefined' || !navigator.locks) {
    return await fn();
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 10000);

  try {
    return await navigator.locks.request(
      name,
      { signal: controller.signal },
      async () => {
        clearTimeout(timer);
        return await fn();
      },
    );
  } catch {
    // Lock acquisition timed out or was aborted — proceed without it
    clearTimeout(timer);
    return await fn();
  }
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    lock: acquireLock,
  },
});